import type { Dispatch, SetStateAction } from "react";
import React, { useState } from "react";
import { DialogFooter } from "../common/design-system/Dialog";
import InfoFill from "../../assets/svgIcons/info_fill.svg";
import UserPlus from "../../assets/svgIcons/user-plus.svg";
import { Input } from "../common/design-system/Input";
import { Button } from "../common/design-system/Button";
import type { ProcessedIntegration } from "../../typing/shared";
import { IntegrationName, ActionType } from "../../typing/shared";
import { useCarbon } from "../../context/CarbonContext";
import {
  generateRequestId,
  getBaseURL,
  getConnectRequestProps,
  getIntegrationDisclaimer,
} from "../../utils/helper-functions";
import { ENV } from "../../constants/shared";
import type { BannerState } from "../common/Banner";
import Banner from "../common/Banner";

export default function Document360Screen({
  processedIntegration,
  setShowAdditionalStep,
}: {
  processedIntegration: ProcessedIntegration;
  setShowAdditionalStep: Dispatch<SetStateAction<boolean>>;
}) {
  const [accountEmail, setAccountEmail] = useState("");
  const [token, setToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [bannerState, setBannerState] = useState<BannerState>({
    message: null,
  });
  const integrationName = IntegrationName.DOCUMENT360;

  const carbonProps = useCarbon();
  const {
    accessToken,
    apiURL,
    authenticatedFetch,
    environment = ENV.PRODUCTION,
    onError,
    onSuccess,
    orgName,
    requestIds,
    setRequestIds,
    useRequestIds,
    whiteLabelingData,
  } = carbonProps;

  const connectDocument360 = async () => {
    try {
      if (!accountEmail) {
        setBannerState({
          message: "Please enter your account email",
          type: "ERROR",
        });

        return;
      }

      if (!token) {
        setBannerState({
          message: "Please enter your access token.",
          type: "ERROR",
        });

        return;
      }

      onSuccess &&
        onSuccess({
          status: 200,
          data: null,
          action: ActionType.INITIATE,
          event: ActionType.INITIATE,
          integration: integrationName,
        });
      setIsLoading(true);

      let requestId = null;

      if (useRequestIds) {
        requestId = generateRequestId(20);
        setRequestIds({
          ...requestIds,
          [processedIntegration?.data_source_type]: requestId,
        });
      }

      const requestObject = getConnectRequestProps(
        processedIntegration,
        requestId,
        { account_email: accountEmail, access_token: token },
        carbonProps,
      );

      const response = await authenticatedFetch(
        `${getBaseURL(apiURL, environment)}/integrations/document360`,
        {
          method: "POST",
          headers: {
            Authorization: `Token ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestObject),
        },
      );

      const responseData = await response.json();

      if (response.status === 200) {
        setBannerState({
          message: `${integrationName} sync initiated, you will be redirected shortly!`,
          type: "SUCCESS",
        });
        setToken("");
        setAccountEmail("");
        setTimeout(() => setShowAdditionalStep(false), 3000);
      } else {
        setBannerState({ type: "ERROR", message: responseData.detail });
        onError &&
          onError({
            status: 400,
            data: [{ message: responseData.detail }],
            action: ActionType.ERROR,
            event: ActionType.ERROR,
            integration: integrationName,
          });
      }

      setIsLoading(false);
    } catch (error) {
      setBannerState({
        type: "ERROR",
        message: `Error connecting your ${integrationName}. Please try again.`,
      });
      setIsLoading(false);
      onError &&
        onError({
          status: 400,
          data: [
            {
              message: `Error connecting your ${integrationName}. Please try again.`,
            },
          ],
          action: ActionType.ERROR,
          event: ActionType.ERROR,
          integration: integrationName,
        });
    }
  };

  return (
    <>
      <Banner bannerState={bannerState} setBannerState={setBannerState} />
      <div className="cc-p-4 cc-min-h-0 cc-flex-grow">
        <div className="cc-p-2 cc-rounded-md cc-bg-surface-surface_1 cc-inline-block cc-mb-5 dark:cc-bg-svg-background cc-cursor-pointer">
          <img
            alt="User Plus"
            className="cc-h-6 cc-w-6 dark:cc-invert-[1] dark:cc-hue-rotate-180"
            src={UserPlus}
          />
        </div>
        <div className="cc-text-base cc-font-semibold cc-mb-5 dark:cc-text-dark-text-white">
          Please enter {integrationName}{" "}
          <span className="cc-px-2 cc-mx-1 cc-bg-surface-info_accent_1 cc-text-info_em cc-rounded-md dark:cc-text-[#88E7FC] dark:cc-bg-[#10284D]">
            account email
          </span>
          and
          <span className="cc-px-2 cc-mx-1 cc-bg-surface-info_accent_1 cc-text-info_em cc-rounded-md dark:cc-text-[#88E7FC] dark:cc-bg-[#10284D]">
            access token
          </span>
          of the acount you wish to connect.
        </div>
        <Input
          className="cc-mb-4"
          onChange={(e) => setAccountEmail(e.target.value)}
          placeholder="Account Email"
          type="text"
          value={accountEmail}
        />
        <Input
          className="cc-mb-32"
          onChange={(e) => setToken(e.target.value)}
          placeholder="Token"
          type="password"
          value={token}
        />
      </div>
      <DialogFooter>
        <div className="cc-flex cc-mb-4 cc-gap-2 cc-items-center">
          <img
            alt="Info Icon"
            className="cc-w-5 cc-shrink-0 dark:cc-invert-[1] dark:cc-hue-rotate-180"
            src={InfoFill}
          />
          <p className="cc-text-low_em cc-font-semibold cc-text-sm dark:cc-text-dark-text-white">
            {getIntegrationDisclaimer(
              processedIntegration,
              whiteLabelingData,
              orgName,
            )}
          </p>
        </div>
        <Button
          className="cc-w-full"
          disabled={isLoading}
          onClick={async () => connectDocument360()}
          size="lg"
          variant="primary"
        >
          Submit
        </Button>
      </DialogFooter>
    </>
  );
}
